import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Search as SearchIcon, TrendingDown } from "lucide-react";

const REASON_META = {
  "error": { label: "Error", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  "aborted": { label: "Abandoned", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  "rate-limited": { label: "Rate-limited", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  "no-citations": { label: "No sources", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  "thin-citations": { label: "Thin sources", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
};

export default function Gaps() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [minCites, setMinCites] = useState(3);
  const [lookback, setLookback] = useState(30);

  const load = async () => {
    const [gaps, s] = await Promise.all([
      api.get("/gaps", { params: { min_citations: minCites, lookback_days: lookback, limit: 100 } }),
      api.get("/gaps/stats", { params: { lookback_days: lookback } }),
    ]);
    setData(gaps.data);
    setStats(s.data);
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [minCites, lookback]);

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Gaps report</p>
        <h1 className="font-display text-3xl">What your knowledge base can&apos;t answer</h1>
        <p className="mt-2 text-muted-foreground">
          Every question your team asked that returned errors, was abandoned, or had thin sources —
          ranked. Write SOPs to close the biggest gaps first.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard label="Chats (last N days)" value={stats?.total ?? 0} tid="gaps-stat-total" />
        <StatCard label="Errors" value={stats?.errors ?? 0} tid="gaps-stat-errors" tone="destructive" />
        <StatCard label="Abandoned" value={stats?.aborted ?? 0} tid="gaps-stat-aborted" tone="warn" />
        <StatCard label="Rate-limited" value={stats?.rate_limited ?? 0} tid="gaps-stat-ratelimited" tone="warn" />
        <StatCard label="Zero sources" value={stats?.no_citations ?? 0} tid="gaps-stat-noc" tone="destructive" />
      </div>

      <div className="card-flat p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="lookback" className="text-xs uppercase tracking-wider text-muted-foreground">Lookback (days)</Label>
            <Input id="lookback" type="number" value={lookback} min={1} max={365} onChange={(e) => setLookback(Number(e.target.value) || 30)} data-testid="gaps-lookback-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mincites" className="text-xs uppercase tracking-wider text-muted-foreground">Thin-source threshold</Label>
            <Input id="mincites" type="number" value={minCites} min={0} max={20} onChange={(e) => setMinCites(Number(e.target.value) || 0)} data-testid="gaps-mincites-input" />
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {data ? `${data.total_gaps} gaps across ${data.total_scanned} chats` : "loading…"}
          </div>
        </div>
      </div>

      {(!data || data.gaps.length === 0) ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm" data-testid="gaps-empty">
          <TrendingDown className="w-6 h-6 mx-auto mb-3 opacity-60" />
          No gaps in this window. Your knowledge base is covering the questions being asked.
        </div>
      ) : (
        <div className="card-flat divide-y divide-border/60">
          {data.gaps.map((g, i) => (
            <div key={i} className="p-5" data-testid={`gap-row-${i}`}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <p className="text-sm font-medium leading-snug flex-1">
                  <SearchIcon className="w-3.5 h-3.5 inline mr-2 text-muted-foreground" />
                  {g.question}
                </p>
                <Badge variant="outline" className="shrink-0 font-mono text-[10px]">×{g.count}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {g.reasons.map((r) => (
                  <Badge key={r} variant="outline" className={`text-[10px] ${REASON_META[r]?.cls || ""}`}>
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {REASON_META[r]?.label || r}
                  </Badge>
                ))}
                {g.top_departments.map((d) => (
                  <span key={d.department} className="text-[10px] font-mono text-muted-foreground border border-border/60 rounded px-2 py-0.5">
                    {d.department} · {d.n}
                  </span>
                ))}
                <span className="text-[10px] text-muted-foreground ml-auto">
                  last: {g.last_seen ? new Date(g.last_seen).toLocaleDateString() : "-"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tid, tone }) {
  const toneCls = tone === "destructive" ? "text-destructive" : tone === "warn" ? "text-amber-500" : "";
  return (
    <div className="card-flat p-5" data-testid={tid}>
      <p className="eyebrow mb-3">{label}</p>
      <p className={`font-display text-3xl ${toneCls}`}>{value}</p>
    </div>
  );
}
